// src/components/layout/AppShell.jsx
// STATUS: active | UPDATED: 2026-08-02
// LOG:
//   2026-08-02  Subtle footer added: AFIP Industry Member 2026 badge + line,
//               per Jazz (industry affiliate, approved to display). Image
//               hides itself if the asset is missing so a deploy never shows
//               a broken image. Header standardized (e765830).
// Outer shell for all authenticated routes. Sidebar on desktop, fixed bottom
// nav on mobile, main content area between them.
//
// The mobile bottom bar is position: fixed, so the main content gets extra
// bottom padding below lg. Without it the bar covers the last chunk of every
// page and users cannot scroll past it.

import { Outlet, useLocation } from 'react-router-dom'
import ScrollToTop from './ScrollToTop'
import CoachDock from './CoachDock'
import DealershipAccent from './DealershipAccent'
import { Box, Flex, HStack, Text, Image, Alert, AlertIcon } from '@chakra-ui/react'
import Sidebar from './Sidebar'
import Header from './Header'
import MobileNav from './MobileNav'
import { useAuth } from '../../hooks/useAuth'

// A signed-in user with no row in public.users. Creating someone in Supabase
// Authentication does not create their profile: there is no trigger, and the
// profile row is what carries role, dealership and name. So they authenticate
// successfully and then land in an app that greets them as "there", shows an
// empty dealership and gives the coach nobody to talk to.
//
// This happened to the platform admin account on 2026-08-28 and presented as
// four unrelated bugs. The silence was the expensive part, so it says so now.
function ProfileMissingNotice() {
  return (
    <Alert status="warning" borderRadius="md" fontSize="sm" mb={4} alignItems="flex-start">
      <AlertIcon />
      <Box>
        <Text fontWeight={600}>Your account has no profile yet.</Text>
        <Text color="inkMuted" mt={1}>
          You are signed in, but there is no record of your name, role or dealership,
          so most of the Academy will look empty. An admin needs to finish creating
          your profile.
        </Text>
      </Box>
    </Alert>
  )
}

function Footer() {
  // Not on the coach. That screen is a chat: the composer is pinned to the
  // bottom and the mobile tab bar is fixed under it, so a marketing footer has
  // nowhere to go and lands underneath the tab bar. Measured on a 375px phone
  // before this: the badge text sat at y716 running to 749, and the tab bar
  // starts at 734, so the last fifteen pixels of it were behind the bar.
  //
  // Padding does not fix that. The footer is the last element on the page, so
  // its own bottom padding pushes nothing out of the way, it just makes the
  // page taller below the thing already covering it.
  const { pathname } = useLocation()
  if (pathname.startsWith('/coach')) return null

  return (
    <HStack
      as="footer"
      justify="center"
      spacing={2.5}
      pt={2}
      pb={{ base: 'calc(6rem + env(safe-area-inset-bottom))', lg: 8 }}
      px={6}
      opacity={0.75}
    >
      <Image
        src="/afip-industry-member-2026.png"
        alt="AFIP Industry Member 2026"
        boxSize="30px"
        objectFit="contain"
        onError={(e) => {
          e.currentTarget.style.display = 'none'
        }}
      />
      <Text fontSize="mono-xs" fontFamily="mono" color="inkDim" letterSpacing="0.04em">
        Janda Dealer Training · AFIP Industry Member 2026
      </Text>
    </HStack>
  )
}

export default function AppShell() {
  const { profileMissing } = useAuth()
  return (
    <Flex minH="100vh" bg="bg">
      <ScrollToTop />
      <DealershipAccent />
      <Box display={{ base: 'none', lg: 'block' }}>
        <Sidebar />
      </Box>

      <Flex direction="column" flex={1} minW={0}>
        <Header />
        {/* base went 6 (24px) to 4 (16px) to 2 (8px). Tyler wants the phone
          using as much of its width as it can, and he is right: every card
          inside already carries its own padding, so the shell's inset was
          being paid twice before a word was drawn. 8px a side leaves 359 of
          375, and the cards still have their own breathing room inside that.
          Not zero, because text running into the bezel reads as broken rather
          than as full width. */}
        <Box as="main" flex={1} p={{ base: 2, lg: 10 }} pb={{ base: 6, lg: 6 }}>
          {profileMissing && <ProfileMissingNotice />}
          <Outlet />
        </Box>
        <Footer />
      </Flex>

      <MobileNav />
      <CoachDock />
    </Flex>
  )
}
